import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { useToast } from '../../../hooks/useToast';
import { getFunctions, httpsCallable } from 'firebase/functions';

interface AIAnalysis {
  sentiment: 'positive' | 'neutral' | 'negative';
  confidence: number;
  shouldFeature: boolean;
  suggestedStatus: 'approved' | 'pending' | 'hidden';
  reasoning: string;
}

interface Testimonial {
  id: string;
  userName: string;
  rating: number;
  reviewText: string;
  isFeatured: boolean;
  submittedAt: any;
  status: 'approved' | 'pending' | 'hidden';
  aiAnalysis?: AIAnalysis;
  analyzedAt?: any;
  aiAnalysisError?: string;
}

type FilterType = 'all' | 'featured' | 'approved';

const TestimonialsEditorPage: React.FC = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const [reanalyzing, setReanalyzing] = useState<string | null>(null);
  const [expandedAnalysis, setExpandedAnalysis] = useState<string | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'testimonials'),
        orderBy('submittedAt', 'desc')
      );
      const querySnapshot = await getDocs(q);

      const data: Testimonial[] = [];
      querySnapshot.forEach((doc) => {
        data.push({ id: doc.id, ...doc.data() } as Testimonial);
      });

      setTestimonials(data);
    } catch (error) {
      console.error('Error loading testimonials:', error);
      showToast({ type: 'error', message: 'Failed to load testimonials' });
    } finally {
      setLoading(false);
    }
  };

  const toggleFeatured = async (id: string, currentStatus: boolean) => {
    try {
      const docRef = doc(db, 'testimonials', id);
      await updateDoc(docRef, { isFeatured: !currentStatus });

      setTestimonials(
        testimonials.map((t) =>
          t.id === id ? { ...t, isFeatured: !currentStatus } : t
        )
      );

      showToast({
        type: 'success',
        message: !currentStatus ? 'Added to featured' : 'Removed from featured',
      });
    } catch (error) {
      console.error('Error updating testimonial:', error);
      showToast({ type: 'error', message: 'Failed to update testimonial' });
    }
  };

  const updateStatus = async (
    id: string,
    newStatus: 'approved' | 'pending' | 'hidden'
  ) => {
    try {
      const docRef = doc(db, 'testimonials', id);
      await updateDoc(docRef, { status: newStatus });

      setTestimonials(
        testimonials.map((t) => (t.id === id ? { ...t, status: newStatus } : t))
      );

      showToast({ type: 'success', message: 'Status updated' });
    } catch (error) {
      console.error('Error updating status:', error);
      showToast({ type: 'error', message: 'Failed to update status' });
    }
  };

  const deleteTestimonial = async (id: string) => {
    if (!confirm('Are you sure you want to delete this testimonial?')) return;

    try {
      await deleteDoc(doc(db, 'testimonials', id));
      setTestimonials(testimonials.filter((t) => t.id !== id));
      showToast({ type: 'success', message: 'Testimonial deleted' });
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      showToast({ type: 'error', message: 'Failed to delete testimonial' });
    }
  };

  const reanalyzeTestimonial = async (id: string) => {
    setReanalyzing(id);
    try {
      const functions = getFunctions(undefined, 'asia-southeast1');
      const reanalyze = httpsCallable(functions, 'reanalyzeTestimonial');

      const result = await reanalyze({ testimonialId: id });
      const data = result.data as { success: boolean; analysis: AIAnalysis };

      if (data.success) {
        // Reload testimonials to get updated data
        await loadTestimonials();
        showToast({
          type: 'success',
          message: 'Re-analysis complete',
        });
      }
    } catch (error) {
      console.error('Error re-analyzing testimonial:', error);
      showToast({
        type: 'error',
        message: 'Failed to re-analyze testimonial',
      });
    } finally {
      setReanalyzing(null);
    }
  };

  const toggleAnalysisExpanded = (id: string) => {
    setExpandedAnalysis(expandedAnalysis === id ? null : id);
  };

  const filteredTestimonials = testimonials.filter((t) => {
    if (filter === 'featured') return t.isFeatured;
    if (filter === 'approved') return t.status === 'approved';
    return true;
  });

  const renderStars = (rating: number) => {
    return (
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`w-5 h-5 ${star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
            viewBox="0 0 24 24"
          >
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        ))}
      </div>
    );
  };

  const getSentimentColor = (sentiment?: string) => {
    switch (sentiment) {
      case 'positive':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'negative':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'neutral':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Testimonials Editor
        </h1>
        <p className="text-gray-600">
          Manage and moderate customer testimonials
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 mb-1">Total Testimonials</p>
          <p className="text-3xl font-bold text-gray-900">
            {testimonials.length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 mb-1">Featured</p>
          <p className="text-3xl font-bold text-yellow-600">
            {testimonials.filter((t) => t.isFeatured).length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 mb-1">Approved</p>
          <p className="text-3xl font-bold text-green-600">
            {testimonials.filter((t) => t.status === 'approved').length}
          </p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setFilter('all')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                filter === 'all'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              All ({testimonials.length})
            </button>
            <button
              onClick={() => setFilter('featured')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                filter === 'featured'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Featured ({testimonials.filter((t) => t.isFeatured).length})
            </button>
            <button
              onClick={() => setFilter('approved')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                filter === 'approved'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Approved (
              {testimonials.filter((t) => t.status === 'approved').length})
            </button>
          </nav>
        </div>
      </div>

      {/* Testimonials List */}
      {filteredTestimonials.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500 text-lg">No testimonials found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredTestimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="bg-white rounded-lg shadow p-6"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {testimonial.userName}
                    </h3>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded ${
                        testimonial.status === 'approved'
                          ? 'bg-green-100 text-green-700'
                          : testimonial.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {testimonial.status}
                    </span>
                    {testimonial.isFeatured && (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded">
                        ⭐ Featured
                      </span>
                    )}
                    {testimonial.aiAnalysis && (
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded border ${getSentimentColor(testimonial.aiAnalysis.sentiment)}`}
                      >
                        AI: {testimonial.aiAnalysis.sentiment} (
                        {testimonial.aiAnalysis.confidence}%)
                      </span>
                    )}
                    {testimonial.aiAnalysisError && (
                      <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded">
                        AI Error
                      </span>
                    )}
                  </div>
                  {renderStars(testimonial.rating)}
                  <p className="text-sm text-gray-500 mt-1">
                    Submitted:{' '}
                    {new Date(
                      testimonial.submittedAt?.toDate()
                    ).toLocaleDateString()}
                    {testimonial.analyzedAt && (
                      <>
                        {' '}
                        • Analyzed:{' '}
                        {new Date(
                          testimonial.analyzedAt?.toDate()
                        ).toLocaleDateString()}
                      </>
                    )}
                  </p>
                </div>
              </div>

              {/* AI Analysis Details */}
              {testimonial.aiAnalysis && (
                <div className="mb-4">
                  <button
                    onClick={() => toggleAnalysisExpanded(testimonial.id)}
                    className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <svg
                      className={`w-4 h-4 transition-transform ${expandedAnalysis === testimonial.id ? 'rotate-90' : ''}`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {expandedAnalysis === testimonial.id
                      ? 'Hide AI Analysis'
                      : 'Show AI Analysis'}
                  </button>

                  {expandedAnalysis === testimonial.id && (
                    <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">
                            Sentiment
                          </p>
                          <p className="text-sm font-medium capitalize">
                            {testimonial.aiAnalysis.sentiment}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">
                            Confidence
                          </p>
                          <p className="text-sm font-medium">
                            {testimonial.aiAnalysis.confidence}%
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">
                            AI Recommended
                          </p>
                          <p className="text-sm font-medium">
                            {testimonial.aiAnalysis.shouldFeature
                              ? '⭐ Feature'
                              : "Don't feature"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">
                            Suggested Status
                          </p>
                          <p className="text-sm font-medium capitalize">
                            {testimonial.aiAnalysis.suggestedStatus}
                          </p>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Reasoning</p>
                        <p className="text-sm text-gray-700 italic">
                          "{testimonial.aiAnalysis.reasoning}"
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Review Text */}
              <p className="text-gray-700 mb-4 italic">
                "{testimonial.reviewText}"
              </p>

              {/* Actions */}
              <div className="flex items-center gap-4 pt-4 border-t border-gray-200 flex-wrap">
                <button
                  onClick={() =>
                    toggleFeatured(testimonial.id, testimonial.isFeatured)
                  }
                  className={`text-sm font-medium ${
                    testimonial.isFeatured
                      ? 'text-yellow-600 hover:text-yellow-700'
                      : 'text-gray-600 hover:text-gray-700'
                  }`}
                >
                  {testimonial.isFeatured ? '★ Featured' : '☆ Feature'}
                </button>

                <button
                  onClick={() => reanalyzeTestimonial(testimonial.id)}
                  disabled={reanalyzing === testimonial.id}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  {reanalyzing === testimonial.id ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                      Re-analyzing...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Re-analyze
                    </>
                  )}
                </button>

                <button
                  onClick={() => deleteTestimonial(testimonial.id)}
                  className="text-sm font-medium text-red-600 hover:text-red-700 ml-auto"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TestimonialsEditorPage;
